import React from 'react';
import {
  Box,
  Button,
  InputLabel,
  Popover,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import { format } from 'date-fns';
import { useField } from 'formik';
import { cx } from '../../helpers/global';

interface Props {
  name: string;
  label: string;
  disabled: boolean;
  hidden: boolean;
  minDate?: Date;
}

const DEFAULT_VALUE = null;
const PLACEHOLDER_DATE = 'MM/DD/YYYY';

const BUTTON_SX: SxProps<Theme> = {
  p: 1,
  fontSize: theme => theme.typography.body2.fontSize,
};

function DateInput({ name, label, disabled, hidden, minDate }: Props) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);

  const [{ value }, { error, touched }, { setTouched, setValue }] =
    useField(name);
  const isError = touched && !!error;
  const [tempValue, setTempValue] = React.useState<Date | null>(
    (value ?? DEFAULT_VALUE) as Date | null,
  );

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setTempValue(value ?? DEFAULT_VALUE);
  };

  const onClose = () => {
    setAnchorEl(null);
    setTouched(true);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-input-popover' : undefined;

  const onChange = (newDate: Date | null) => {
    setTempValue(newDate ? new Date(newDate) : null);
  };

  const onClear = () => {
    setTempValue(DEFAULT_VALUE);
  };

  const onApply = () => {
    setValue(tempValue, true);
    setTimeout(() => onClose(), 0);
  };

  return (
    <Box
      className={cx('input', hidden && 'hidden', isError && 'error')}
      position="relative">
      <InputLabel htmlFor={name}>
        <Typography variant="h5">{label}</Typography>
      </InputLabel>
      <input
        id={name}
        name={name}
        onClick={onClick}
        value={`${value ? format(value, 'MM/dd/yyyy') : PLACEHOLDER_DATE}`}
        disabled={disabled}
        readOnly
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}>
        <DateCalendar value={tempValue} onChange={onChange} minDate={minDate} />
        <Box
          p={2}
          pt={0}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={1}>
          <Button
            onClick={onClear}
            variant="light"
            color="info"
            type="button"
            sx={BUTTON_SX}>
            Clear
          </Button>
          <Button
            disabled={!tempValue}
            onClick={onApply}
            variant="base"
            color="primary"
            type="button"
            sx={BUTTON_SX}>
            Apply
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}

export default DateInput;
